import React from 'react';
import { Music2 } from 'lucide-react';

interface MediaPreviewProps {
  url: string;
  type: string;
  fileName?: string;
  className?: string;
  onClick?: () => void;
  preserveAspectRatio?: boolean;
}

export function MediaPreview({ url, type, fileName, className = '', onClick, preserveAspectRatio = true }: MediaPreviewProps) {
  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onClick?.();
  };

  if (type === 'video') {
    return (
      <div className={`w-full flex justify-center bg-black rounded-lg overflow-hidden ${className}`}>
        <video
          src={url}
          controls
          className={`${preserveAspectRatio ? 'max-w-full max-h-[600px] w-auto h-auto' : 'w-full h-full object-cover'} cursor-pointer`}
          onClick={handleClick}
          preload="metadata"
          style={preserveAspectRatio ? { maxHeight: '600px' } : undefined}
        />
      </div>
    );
  }

  if (type === 'audio') {
    return (
      <div className={`w-full rounded-lg bg-gray-50 p-4 ${className}`}>
        <div className="flex items-center gap-3 mb-2">
          <Music2 className="w-6 h-6 text-blue-500" />
          <span className="text-sm text-gray-600">{fileName || 'Audio file'}</span>
        </div>
        <audio controls className="w-full">
          <source src={url} type="audio/*" />
          Your browser does not support the audio element.
        </audio>
      </div>
    );
  }

  const isGif = type === 'gif' || (url && /\.gif(\?|$)/i.test(url)) || (url && (url.includes('.giphy.com') || url.includes('tenor.com')));

  if (isGif) {
    return (
      <div 
        className={`relative cursor-pointer overflow-hidden rounded-lg bg-gray-100 ${className}`}
        onClick={handleClick}
      >
        <img
          src={url}
          alt={fileName || "GIF"}
          className={`${preserveAspectRatio ? 'w-full h-auto object-contain max-h-[75vh] bg-slate-950/90' : 'w-full h-auto object-cover max-h-[600px]'} transition-transform hover:scale-[1.02]`}
          loading="lazy"
          onError={(e) => {
            const img = e.target as HTMLImageElement;
            img.style.display = 'none';
            console.error('Failed to load GIF:', url);
          }}
        />
        <div className="absolute top-2 left-2 rounded-md bg-purple-600/90 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white backdrop-blur-md shadow-md">
          GIF
        </div>
      </div>
    );
  }

  return (
    <div 
      className={`relative cursor-pointer overflow-hidden rounded-lg bg-gray-100 ${className}`}
      onClick={handleClick}
    >
      <img
        src={url}
        alt={fileName || ""}
        className={`${preserveAspectRatio ? 'w-full h-auto object-contain max-h-[75vh] bg-slate-950/90' : 'w-full h-auto object-cover max-h-[600px]'} transition-transform hover:scale-[1.02]`}
        loading="lazy"
        onError={(e) => {
          const img = e.target as HTMLImageElement;
          img.style.display = 'none';
          console.error('Failed to load image:', url);
        }}
      />
    </div>
  );
}